/** @jsx jsx */
import { useMutation } from '@apollo/react-hooks';
import { css, jsx } from '@emotion/core';
import React from 'react';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import Button from '../../components/form/Button';
import InputField from '../../components/form/InputField';
import Message, {
	MessageProps,
	MessageType
} from '../../components/form/Message';
import Container from '../../components/layout/Container';
import { GET_AUTH_USER, LOGIN } from '../../graphql/auth';
import styled from '../../styled';

interface FormProps {
	timeout?: number;
}

const Form = styled('form')<FormProps>`
	position: absolute;
	top: 50vh;
	left: 0;
	width: 100%;
	min-height: 50vh;
	opacity: 1;
	z-index: 9999;
	background-color: ${props => props.theme.color.lightgrey};
	transform: translate(0, 0);
	transition: all ${props => props.timeout}ms ease-out;

	&.lf-appear,
	&.lf-enter {
		transform: translate(0, 10vh);
		opacity: 0;
		background-color: transparent;
	}

	&.lf-appear-active,
	&.lf-enter-active {
	}

	&.lf-enter-done {
	}

	&.lf-exit {
	}

	&.lf-exit-active {
		position: fixed;
		transform: translate(0, 50vh);
		background-color: transparent;
		opacity: 0;
	}
`;

const Inner = styled('div')`
	max-width: 40em;
`;

const fieldClasses = {
	actions: {
		root: css`
			display: flex;
			justify-content: space-between;
		`
	},
	notifications: css`
		transition: height 300ms ease;
		margin-right: auto;
		color: red;
	`,
	input: css`
		margin-bottom: 1em;
	`
};

interface LoginProps extends RouteComponentProps {
	show: boolean;
	messages?: MessageProps[];
}

const Login: React.FC<LoginProps> = ({ show, location, history }) => {
	const urlParams = new URLSearchParams(location.search);
	const initialMessage = urlParams.get('m');

	const [email, setEmail] = React.useState('');
	const [password, setPassword] = React.useState('');
	const [messages, setMessages] = React.useState<MessageProps[]>([]);

	const addMessage = (
		type: 'error' | 'warning' | 'message',
		message: string
	) => {
		setMessages(
			messages
				.filter(m => m.isActive)
				.map(m => ({ ...m, isActive: false }))
				.concat({ type, message, isActive: true })
		);
	};

	React.useEffect(() => {
		if (!initialMessage) {
			return;
		}

		const type = (urlParams.get('t') as MessageType | null) || 'message';
		addMessage(type, initialMessage);
	}, [initialMessage]);

	const [doLogin] = useMutation(LOGIN, {
		variables: { email, password },
		refetchQueries: [{ query: GET_AUTH_USER }]
	});
	const login = async () => {
		try {
			await doLogin();
			setMessages([]);
		} catch (err) {
			let msg: string = err.message.replace('GraphQL error: ', '');
			if (msg === 'Network error: Failed to fetch') {
				msg = 'Could not connect to server';
			}

			addMessage('error', msg);
		}
	};

	const onExit = (node: HTMLElement) => {
		// let's fix the heigt to animate
		const rect = node.getBoundingClientRect();
		node.style.height = rect.height + 'px';
	};
	const onExiting = (node: HTMLElement) => {
		node.style.height = '0px';
	};
	const onExited = (node: HTMLElement) => {
		node.style.height = '';
	};

	return (
		<CSSTransition
			classNames="lf"
			in={show}
			appear
			mountOnEnter
			unmountOnExit
			timeout={300}
			onExit={onExit}
			onExiting={onExiting}
			onExited={onExited}
		>
			<Form
				timeout={300}
				onSubmit={e => {
					e.preventDefault();

					if (!email || !password) {
						return;
					}

					login();
				}}
			>
				<Container>
					<Inner>
						<div css={fieldClasses.notifications}>
							{messages
								? messages.map((message, i) => <Message key={i} {...message} />)
								: null}
						</div>
						<InputField
							label="email"
							name="email"
							placeholder="Email"
							value={email}
							onChange={e => setEmail(e.target.value)}
							css={fieldClasses.input}
						/>
						<InputField
							label="password"
							name="password"
							type="password"
							placeholder="********"
							value={password}
							onChange={e => setPassword(e.target.value)}
							css={fieldClasses.input}
						/>
						<div css={fieldClasses.actions.root}>
							<Button type="button" onClick={() => history.push('/recover')}>
								Reset Password
							</Button>
							<Button type="submit" noBorder hasBackground color="primary">
								Login
							</Button>
						</div>
					</Inner>
				</Container>
			</Form>
		</CSSTransition>
	);
};

export default withRouter(Login);
